import React from 'react'
import { onLogin } from '../../service-worker/service-worker-utils'
import eqxAuthMsal from '../../shared/classes/eqxAuthMsal'
import { eqxEvent } from '../../shared/classes/eqxEventBroker'
import EqxLoading from '../../shared/components/EqxLoading'
import { PageErrorNoRouting } from '../../pages/PageError'
import generateContextProvider, { ContextProviderWrapperProps } from '../../shared/hooks/GenerateContextProvider'
import { useApiContext } from './AppApi'
import { useBaseConfigContext } from './AppBaseConfig'
import { useAuthOAuthContext } from './AppAuthOAuth'
import { clearPostAuthReturn, savePostAuthReturnFromWindow } from './authReturnPath'
import { useDeploymentContext } from './AppDeploymentProvider'

const LS_TOKEN = 'token'
const LS_TOKEN_TENANT = 'token_tenant'
/** Written with JWT so a cached token is only reused for the same tenant + environment + instance. */
const LS_TOKEN_ENV = 'token_environment'
const LS_TOKEN_INSTANCE = 'token_instance'
const USER_ERROR_QUERY_PARAM = 'userError'

function clearContinuumJwtFromStorage() {
    localStorage.removeItem(LS_TOKEN)
    localStorage.removeItem(LS_TOKEN_TENANT)
    localStorage.removeItem(LS_TOKEN_ENV)
    localStorage.removeItem(LS_TOKEN_INSTANCE)
}

function storedJwtMatchesContext(tenantID: string, environment: string, instance: string): boolean {
    const stored = localStorage.getItem(LS_TOKEN)
    if (!stored) return false
    if (localStorage.getItem(LS_TOKEN_TENANT) !== tenantID) return false
    if ((localStorage.getItem(LS_TOKEN_ENV) ?? '') !== environment) return false
    if ((localStorage.getItem(LS_TOKEN_INSTANCE) ?? '') !== instance) return false
    return true
}

interface AppAuth {
    msal: eqxAuthMsal
    token: string | undefined
    login: () => Promise<string>
    logout: () => Promise<void>
    error: string | undefined
    /** True while sign-out is in progress (suppresses AppTokenProvider return-path capture). */
    isLoggingOut: boolean
}

const AppAuthWrapper = (props: ContextProviderWrapperProps<AppAuth>) => {
    const AuthProvider = props.provider

    const { tenantID, environment, instance } = useDeploymentContext()
    const [token, setToken] = React.useState<string | undefined>(() => {
        if (storedJwtMatchesContext(tenantID, environment, instance)) {
            return localStorage.getItem(LS_TOKEN) ?? undefined
        }
        clearContinuumJwtFromStorage()
        return undefined
    })

    const baseConfig = useBaseConfigContext()
    const api = useApiContext()
    const oauth = useAuthOAuthContext()

    const [exchangeError, setExchangeError] = React.useState<string | undefined>()
    const [exchangeInFlight, setExchangeInFlight] = React.useState(false)
    const [isLoggingOut, setIsLoggingOut] = React.useState(false)

    const setTokenInApp = React.useCallback(
        (backendToken?: string) => {
            setToken(backendToken)
            setExchangeError(undefined)
            if (backendToken) {
                localStorage.setItem(LS_TOKEN, backendToken)
                localStorage.setItem(LS_TOKEN_TENANT, tenantID)
                localStorage.setItem(LS_TOKEN_ENV, environment)
                localStorage.setItem(LS_TOKEN_INSTANCE, instance)
            } else {
                clearContinuumJwtFromStorage()
            }
        },
        [tenantID, environment, instance]
    )

    React.useEffect(() => {
        if (!storedJwtMatchesContext(tenantID, environment, instance)) {
            clearContinuumJwtFromStorage()
            setToken(undefined)
        }
    }, [tenantID, environment, instance])

    React.useEffect(() => {
        const clearJwt = () => setTokenInApp(undefined)
        const id426 = api.events.on('response426', function (this: eqxEvent, _response: Response) {
            clearJwt()
        })
        const id401 = api.events.on(
            'response401',
            function (this: eqxEvent, response: Response, opts?: { isBackground?: boolean }) {
                if (opts?.isBackground) return
                clearJwt()
            }
        )
        return () => {
            api.events.removeEvent('response426', id426)
            api.events.removeEvent('response401', id401)
        }
    }, [api, setTokenInApp])

    /** Exchange Entra token for Continuum JWT once tenant headers are active. */
    React.useEffect(() => {
        if (!oauth.aadAccessToken) return

        const stored = localStorage.getItem(LS_TOKEN)
        if (stored && storedJwtMatchesContext(tenantID, environment, instance) && token === stored) return

        let cancelled = false
        setExchangeInFlight(true)
        setExchangeError(undefined)

        const runExchange = async () => {
            let aad = oauth.aadAccessToken
            if (!aad) {
                aad = (await oauth.refreshAadAccessToken()) ?? ''
            }

            if (cancelled) return

            if (!aad) {
                setExchangeError(
                    'Your Microsoft session could not be refreshed. Go to the app root to sign in again.'
                )
                return
            }

            try {
                const tokenResponse = await api.endpoints.token().execute(api, {
                    aadToken: aad,
                    version: baseConfig.version,
                })
                if (cancelled) return
                setTokenInApp(tokenResponse.accessToken)
                await onLogin(tokenResponse.accessToken, api).catch((error) => {
                    console.error('Failed to cache initial data:', error)
                })
            } catch (error) {
                console.error('Failed to exchange token:', error)
                if (!cancelled) {
                    const params = new URLSearchParams()
                    params.set(USER_ERROR_QUERY_PARAM, '1')
                    window.location.assign(`/switch?${params.toString()}`)
                }
            }
        }

        void runExchange().finally(() => {
            setExchangeInFlight(false)
        })

        return () => {
            cancelled = true
        }
    }, [
        oauth.aadAccessToken,
        oauth.refreshAadAccessToken,
        tenantID,
        environment,
        instance,
        api,
        baseConfig.version,
        token,
        setTokenInApp,
    ])

    const compositeError = exchangeError ?? oauth.oauthError

    const storedNow = localStorage.getItem(LS_TOKEN)
    const backendTokenOk = !!(
        storedNow &&
        token === storedNow &&
        storedJwtMatchesContext(tenantID, environment, instance)
    )

    /** Block router until exchange runs; avoids AppTokenProvider firing assign('/') on frame 1 (effect runs later). */
    const awaitingBackendJwt =
        !!oauth.aadAccessToken && !backendTokenOk && compositeError === undefined

    const auth: AppAuth = {
        msal: oauth.msal,
        token,
        login: () => {
            savePostAuthReturnFromWindow()
            window.location.assign('/')
            return Promise.resolve('')
        },
        logout: async () => {
            setIsLoggingOut(true)
            setTokenInApp(undefined)
            clearPostAuthReturn()
            try {
                // MSAL logoutRedirect navigates to Entra then postLogoutRedirectUri; do not assign('/') here.
                await oauth.logoutMsal()
            } finally {
                clearPostAuthReturn()
                setIsLoggingOut(false)
            }
        },
        error: compositeError,
        isLoggingOut,
    }

    if (exchangeInFlight || awaitingBackendJwt) {
        return <EqxLoading message="Logging in..." />
    }

    if (compositeError && !token) {
        return <PageErrorNoRouting error={compositeError} />
    }

    return <AuthProvider value={auth}>{props.children}</AuthProvider>
}

const { ContextProvider: AppAuthProvider, useContext: useAuthContext } = generateContextProvider<AppAuth>(
    undefined,
    AppAuthWrapper
)

export { AppAuthProvider as default, useAuthContext, type AppAuth }
